export const TYPE_DEPLOY = {
  KUBERNETES: "kubernetes",
  GKE: "gke",
  GKE_AUTOPILOT: "gke-autopilot",
  AKS: "aks",
  EKS: "eks",
  EC2: "ec2",
  VM: "vm",
  ECS: "ecs",
  ECS_FARGATE: "ecs-fargate",
  EKS_FARGATE: "eks-fargate",
  OPENSHIFT: "openshift",
  LAMBDA: "lambda",
  AZURE_APPS: "azure-apps",
  AGENTCORE: "agentcore",
  FOUNDRY: "foundry",
  COPILOT_STUDIO: "copilot-studio"
}

// Tab opened for /new-deployment and for a deploy type not supported here
export const DEFAULT_DEPLOY_TAB = TYPE_DEPLOY.KUBERNETES

// EC2 is left out: its tab sets up side scan, which self-hosted does not run
export const SELF_HOSTED_SUPPORTED_DEPLOY_TYPES = [
  TYPE_DEPLOY.KUBERNETES,
  TYPE_DEPLOY.GKE,
  TYPE_DEPLOY.AKS,
  TYPE_DEPLOY.EKS,
  TYPE_DEPLOY.OPENSHIFT,
  TYPE_DEPLOY.VM
] as const

export const ALL_SUPPORTED_DEPLOY_TYPES = [
  TYPE_DEPLOY.KUBERNETES,
  TYPE_DEPLOY.GKE,
  TYPE_DEPLOY.GKE_AUTOPILOT,
  TYPE_DEPLOY.AKS,
  TYPE_DEPLOY.EKS,
  TYPE_DEPLOY.EKS_FARGATE,
  TYPE_DEPLOY.EC2,
  TYPE_DEPLOY.VM,
  TYPE_DEPLOY.ECS,
  TYPE_DEPLOY.ECS_FARGATE,
  TYPE_DEPLOY.OPENSHIFT,
  TYPE_DEPLOY.LAMBDA,
  TYPE_DEPLOY.AZURE_APPS,
  TYPE_DEPLOY.AGENTCORE,
  TYPE_DEPLOY.FOUNDRY,
  TYPE_DEPLOY.COPILOT_STUDIO
] as const

// Offered only where the tenant has AI (its AI Agents page shown, live or demo)
export const AI_DEPLOY_TYPES: readonly string[] = [
  TYPE_DEPLOY.AGENTCORE,
  TYPE_DEPLOY.FOUNDRY,
  TYPE_DEPLOY.COPILOT_STUDIO
]

export const DEFAULT_DOWNLOAD_URL = "/api/v1/deployments/download"

export const REGISTRY_CREDENTIALS_DOWNLOAD_URL =
  "/api/v1/deployments/registry-credentials"

export const REGISTRY_FILENAME = "kortex-regcred"

export const REGCRED_YAML_FILENAME = `${REGISTRY_FILENAME}.yaml`

export const REGCRED_CF_YAML_FILENAME = `${REGISTRY_FILENAME}-cf.yaml`

// Docker config.json with the tenant's Kodem registry credentials
export const REGCRED_DOCKER_JSON_FILENAME = `${REGISTRY_FILENAME}-docker.json`

export const DEFAULT_VALUES = {
  ENVIRONMENT: "ENVIRONMENT",
  DEPLOYMENT_KEY: "DEPLOYMENT_KEY",
  STACK_NAME: "STACK_NAME",
  CLOUDFORMATION_FILE: "CLOUDFORMATION_FILE",
  WORKDIR: "WORKDIR",
  EFS_VOLUME_HANDLE: "EFS_VOLUME_HANDLE",
  AWS_ROLE_ARN: "AWS_ROLE_ARN",
  CLUSTER_ARN: "CLUSTER_ARN",
  SECRET_NAME: "SECRET_NAME",
  KORTEX_SUBNET: "KORTEX_SUBNET",
  KORTEX_SECURITY_GROUP: "KORTEX_SECURITY_GROUP",
  KORTEX_TGZ_PATH: "KORTEX_TGZ_PATH",
  NAMESPACE: "NAMESPACE",
  SECRET_ARN: "SECRET_ARN",
  KODEM_HELM_REPO: "KODEM_HELM_REPO",
  KONTIGO_URL: "KONTIGO_URL",
  KORTEX_INSTALLER_PATH: "KORTEX_INSTALLER_PATH",
  FUNCTION_NAME: "FUNCTION_NAME",
  LAYER_ARN: "LAYER_ARN",
  BUCKET: "BUCKET",
  IMAGE: "IMAGE",
  RESOURCE_GROUP: "RESOURCE_GROUP",
  APP_NAME: "APP_NAME",
  ACR_NAME: "ACR_NAME",
  RUNTIME_ID: "RUNTIME_ID",
  REPOSITORY: "REPOSITORY",
  KEY: "KEY",
  AGENT_DIR: "AGENT_DIR",
  PROJECT_ENDPOINT: "PROJECT_ENDPOINT",
  PROJECT_ID: "PROJECT_ID",
  AGENT_NAME: "AGENT_NAME",
  AGENT_VERSION: "AGENT_VERSION"
}

export const KODEM_HELM_REPO_URL = "https://charts.kodemsecurity.com"

export const SIDE_SCAN_ONBOARDING_TEMPLATE_URL =
  "/v1/deployments/side-scan/onboarding-template"

export const SIDE_SCAN_CF_FILENAME = "kodem-ec2-side-scan-cf.yaml"

export const LAMBDA_SIDE_SCAN_ONBOARDING_TEMPLATE_URL =
  "/v1/deployments/side-scan/lambda/onboarding-template"

export const LAMBDA_SIDE_SCAN_CF_FILENAME = "kodem-lambda-side-scan-cf.yaml"

export const PACKAGE_SPEC_TYPE = {
  VM: "vm",
  EC2: "ec2",
  ECS: "ecs",
  ECS_FARGATE: "ecs-fargate",
  WINDOWS: "windows",
  LAMBDA: "lambda-extension",
  PYTHON: "python"
} as const

export const KODEM_REGISTRY_HOST = "registry.kodemsecurity.com"

// The Kodem registry authenticates with a GCP service-account key
export const KODEM_REGISTRY_USERNAME = "_json_key"

export const KODEM_PUBLIC_REGISTRY = `${KODEM_REGISTRY_HOST}/kodem-public/kortex`

// Name of the deployment key value in deploy commands (gets the generate-key button)
export const DEPLOYMENT_KEY_FIELD = "deploymentKey"

export const PACKAGE_SPEC_ARCH = { AMD64: "amd64", ARM64: "arm64" } as const

// Lambda function architectures, selected as their Linux platform: the layer
// zip arch (packageSpecArch) and the AWS architecture name (lambdaArch)
export const LAMBDA_ARCHITECTURES = [
  {
    platform: "linux_amd64",
    packageSpecArch: PACKAGE_SPEC_ARCH.AMD64,
    lambdaArch: "x86_64"
  },
  {
    platform: "linux_arm64",
    packageSpecArch: PACKAGE_SPEC_ARCH.ARM64,
    lambdaArch: "arm64"
  }
] as const

// The Azure app platforms the Azure Apps tab deploys to, selected first
export const AZURE_APP_PLATFORMS = [
  "appService",
  "containerApps",
  "functions"
] as const

// How an agent runs on an AI agent platform (AgentCore, Foundry), selected first
export const AGENT_DEPLOYMENTS = ["container", "codeUpload"] as const

export const DOCS_SLUG_BY_DEPLOY_TYPE: Record<string, string> = {
  [TYPE_DEPLOY.KUBERNETES]:
    "installing-kodem-runtime-sensors-in-kubernetes-gke-aks-eks",
  [TYPE_DEPLOY.GKE]:
    "installing-kodem-runtime-sensors-in-kubernetes-gke-aks-eks",
  [TYPE_DEPLOY.AKS]:
    "installing-kodem-runtime-sensors-in-kubernetes-gke-aks-eks",
  [TYPE_DEPLOY.EKS]:
    "installing-kodem-runtime-sensors-in-kubernetes-gke-aks-eks",
  [TYPE_DEPLOY.OPENSHIFT]:
    "installing-kodem-runtime-sensors-in-kubernetes-gke-aks-eks",
  [TYPE_DEPLOY.GKE_AUTOPILOT]:
    "installing-kodem-runtime-sensors-in-gke-autopilot",
  [TYPE_DEPLOY.EKS_FARGATE]: "installing-kodem-runtime-sensors-on-eks-fargate",
  [TYPE_DEPLOY.EC2]: "installing-kodem-runtime-sensors-on-ec2",
  [TYPE_DEPLOY.ECS]: "installing-kodem-runtime-sensors-on-ecs",
  [TYPE_DEPLOY.ECS_FARGATE]: "installing-kodem-runtime-sensors-on-ecs-fargate",
  [TYPE_DEPLOY.VM]: "installing-kodem-runtime-sensors-on-vms",
  [TYPE_DEPLOY.LAMBDA]: "installing-kodem-runtime-sensors-on-aws-lambda",
  [TYPE_DEPLOY.AZURE_APPS]: "installing-kodem-runtime-sensors-on-azure-apps",
  [TYPE_DEPLOY.AGENTCORE]:
    "installing-kodem-runtime-sensors-on-amazon-bedrock-agentcore",
  [TYPE_DEPLOY.FOUNDRY]: "installing-kodem-runtime-sensors-on-microsoft-foundry"
}

// VM Windows is OS-dependent (resolved in VmTab), not derivable from the type.
// Private beta; slug will change at GA.
export const VM_WINDOWS_DOCS_SLUG =
  "install-kodem-runtime-sensors-on-windows-vms-private-beta"
